import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Paperclip, Send } from "lucide-react";
import { Field, Pick } from "@/components/inquiries/InquiryFormDialog";
import { localNowInput, notifyInquiriesChanged } from "@/lib/inquiry";

const blank = (s) => ({ at: localNowInput(), autoAt: true, type: s.interaction_types[0], channel: "", user_id: "", outcome: "", notes: "",
  next_action: "", followup_date: "", contact_ids: [], direction: "" });

const DIRECTIONS = [["", "Auto from type"], ["outgoing", "We reached out"], ["incoming", "Contact reached us"], ["internal", "Internal only"]];

export const AddEventForm = ({ inquiry, settings, onAdded, correcting, onCancelCorrect }) => {
  const [f, setF] = useState(blank(settings));
  const [files, setFiles] = useState([]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/users/assignable?team=1").then((r) => setPeople(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (correcting) setF((x) => ({ ...x, type: "Internal remark", notes: `Correction to entry of ${new Date(correcting.at).toLocaleString("en-IN")}: ` })); }, [correcting]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    try {
      const { autoAt, ...rest } = f;
      const body = { ...rest, at: autoAt ? new Date().toISOString() : new Date(f.at).toISOString(), channel: f.channel || null, user_id: f.user_id || null, direction: f.direction || null,
        followup_date: f.followup_date || null, corrects_event_id: correcting?.id || null };
      const { data: ev } = await api.post(`/inquiries/${inquiry.id}/events`, body);
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/inquiries/${inquiry.id}/events/${ev.id}/attachments`, fd);
      }
      toast.success("Added to the journey");
      setF(blank(settings)); setFiles([]); onCancelCorrect?.(); notifyInquiriesChanged(); onAdded();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-blue-100 rounded-md p-4" data-testid="add-event-form">
      <div className="text-sm font-semibold mb-3">{correcting ? "Add a correction" : "Record an interaction"}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Date & time"><Input data-testid="ev-at" type="datetime-local" value={f.at} onChange={(e) => setF((x) => ({ ...x, at: e.target.value, autoAt: false }))} /></Field>
        <Field label="Type"><Pick testid="ev-type" value={f.type} onChange={set("type")} options={settings.interaction_types} /></Field>
        <Field label="Channel"><Pick testid="ev-channel" value={f.channel} onChange={set("channel")} options={settings.channels} placeholder="Optional" /></Field>
        <Field label="Handled by"><Pick testid="ev-user" value={f.user_id} onChange={set("user_id")} options={people.map((p) => ({ value: p.id, label: p.name }))} placeholder="Me" /></Field>
        <Field label="Direction" className="col-span-2 md:col-span-4">
          <div className="flex flex-wrap gap-1.5">
            {DIRECTIONS.map(([v, l]) => <button type="button" key={v} data-testid={`ev-dir-${v || "auto"}`} onClick={() => set("direction")(v)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.direction === v ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700 hover:border-blue-300"}`}>{l}</button>)}
          </div>
        </Field>
        <Field label="Notes / discussion" className="col-span-2 md:col-span-4"><Textarea data-testid="ev-notes" rows={2} value={f.notes} onChange={set("notes")} /></Field>
        <Field label="Outcome" className="col-span-2"><Input data-testid="ev-outcome" value={f.outcome} onChange={set("outcome")} placeholder="e.g. Asked for revised quotation" /></Field>
        <Field label="Next action"><Input data-testid="ev-next-action" value={f.next_action} onChange={set("next_action")} /></Field>
        <Field label="Follow-up date"><Input data-testid="ev-followup" type="date" value={f.followup_date} onChange={set("followup_date")} /></Field>
        {inquiry.contacts?.length > 1 && (
          <Field label="Contacts involved" className="col-span-2 md:col-span-4">
            <div className="flex flex-wrap gap-1.5">
              {inquiry.contacts.map((c) => {
                const on = f.contact_ids.includes(c.id);
                return <button type="button" key={c.id} data-testid={`ev-contact-${c.id}`} onClick={() => set("contact_ids")(on ? f.contact_ids.filter((x) => x !== c.id) : [...f.contact_ids, c.id])}
                  className={`text-xs px-2 py-1 rounded-full border ${on ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700"}`}>{c.name}</button>;
              })}
            </div>
          </Field>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <Paperclip className="w-4 h-4" /> {files.length ? `${files.length} file(s)` : "Attach documents"}
          <input data-testid="ev-files" type="file" multiple className="hidden" onChange={(e) => setFiles([...e.target.files])} />
        </label>
        <div className="flex gap-2">
          {correcting && <Button variant="outline" size="sm" onClick={onCancelCorrect}>Cancel correction</Button>}
          <Button data-testid="ev-save" size="sm" disabled={busy || !f.at} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white gap-1"><Send className="w-3.5 h-3.5" />{busy ? "Saving…" : "Add to journey"}</Button>
        </div>
      </div>
    </div>
  );
};
